import React from 'react';
import { motion } from 'framer-motion';
import { mockData } from '../../data/mock';
import { revealVariants, staggerContainer } from '../../utils/motion';
import { useInViewOnce } from '../../hooks/useInViewOnce';

export const TrustedClientsSection = () => {
  const [ref, isInView] = useInViewOnce();

  return (
    <section className="py-20 bg-gradient-to-br from-[#1F3C88]/5 to-slate-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#FF5C8A]/10 to-transparent rounded-full transform translate-x-48 -translate-y-48" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-[#FF8C42]/10 to-transparent rounded-full transform -translate-x-36 translate-y-36" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.div
            variants={revealVariants}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A]/10 to-[#FF8C42]/10 text-[#1F3C88] px-4 py-2 rounded-full text-sm font-medium mb-6"
          >
            <span>Trusted Partners</span>
          </motion.div>
          
          <motion.h2 
            variants={revealVariants}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Trusted by <span className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent">Industry Leaders</span>
          </motion.h2>
          
          <motion.p 
            variants={revealVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            We're honored to partner with leading organizations across multiple industries, 
            delivering exceptional results that drive sustainable business growth.
          </motion.p>
        </motion.div>

        {/* Auto-Scrolling Carousel - Desktop */}
        <motion.div
          variants={revealVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="hidden md:block relative overflow-hidden mb-12"
        >
          <motion.div
            className="flex space-x-8 animate-scroll-infinite"
            style={{
              width: 'max-content',
              animation: 'scroll-infinite 40s linear infinite'
            }}
          >
            {/* Triple the clients for seamless loop */}
            {[...mockData.clients, ...mockData.clients, ...mockData.clients].map((client, index) => (
              <motion.div
                key={`${client.name}-${index}`}
                className="flex-shrink-0 group"
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-48 h-24 bg-white rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-500 border border-gray-100 group-hover:border-[#FF5C8A]/30 relative overflow-hidden">
                  {/* Hover Glow Effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-[#FF5C8A]/5 to-[#FF8C42]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"
                  />
                  
                  {/* Client Name */}
                  <div className="text-center relative z-10">
                    <div className="text-[#1F3C88] font-bold text-lg mb-1 group-hover:text-[#FF5C8A] transition-colors">
                      {client.name.length > 20 
                        ? client.name.substring(0, 20) + '...'
                        : client.name
                      }
                    </div>
                    
                    {/* Animated Underline */}
                    <motion.div
                      className="w-0 h-0.5 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded mx-auto group-hover:w-full transition-all duration-500"
                    />
                  </div>

                  {/* Shimmer Effect */}
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "100%" }}
                    transition={{ duration: 0.8 }}
                  >
                    <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" />
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Client Grid - Mobile */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12"
        >
          {mockData.clients.slice(0, 6).map((client, index) => (
            <motion.div
              key={client.name}
              variants={revealVariants}
              custom={index}
              className="group"
            >
              <div className="w-full h-20 bg-white rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group-hover:border-[#FF5C8A]/30">
                <div className="text-center">
                  <div className="text-[#1F3C88] font-bold text-sm group-hover:text-[#FF5C8A] transition-colors">
                    {client.name.length > 25 
                      ? client.name.substring(0, 25) + '...'
                      : client.name
                    }
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Trust Metrics */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16"
        >
          {[
            { 
              metric: "99%", 
              label: "Client Retention Rate",
              desc: "Long-term partnerships built on trust"
            },
            { 
              metric: "24/7", 
              label: "Support Available", 
              desc: "Round-the-clock project assistance"
            },
            { 
              metric: "100%", 
              label: "Confidentiality Assured",
              desc: "Your business data is secure with us"
            }
          ].map((item, index) => (
            <motion.div
              key={index}
              variants={revealVariants}
              custom={index}
              className="text-center bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              whileHover={{ y: -5 }}
            >
              <motion.div
                className="text-4xl font-bold bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent mb-3"
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : { scale: 0 }}
                transition={{ delay: 0.5 + index * 0.2, type: "spring" }}
              >
                {item.metric}
              </motion.div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-[#1F3C88] transition-colors">
                {item.label}
              </h3>
              
              <p className="text-gray-600 text-sm leading-relaxed">
                {item.desc}
              </p>

              {/* Animated Border */}
              <motion.div
                className="w-0 h-1 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full mx-auto mt-4 group-hover:w-full transition-all duration-500"
              />
            </motion.div>
          ))}
        </motion.div>
      </div>

      <style jsx>{`
        @keyframes scroll-infinite {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
        
        .animate-scroll-infinite:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};